import {
	Card,
	CardHeader,
	CardHeaderActions,
	CardHeading,
	EmptyDataList,
	IconButton,
	Id,
	LoadingBar,
	SortableTreeView,
	TreeItem,
	TreeItems,
	useLaravelCrudEntity,
	useOpen,
} from "@dgs/core";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditPreferenceDrawer } from "../preferences/EditPreferencesDrawer";
import { PreferenceDrawer } from "../preferences/PreferenceDrawer";
import { PreferencesTreeNode } from "../preferences/PreferenceTreeNode";
import { preferenceService } from "../shared/api/preferences";
import { PreferenceIndexResource } from "../shared/types";
import { usePreference } from "./PreferenceProvider";

export function findItem(items: TreeItems, itemId: Id) {
	return items.find(({ id }) => id === itemId);
}

export function buildTree(flattenedItems: PreferenceIndexResource[]): TreeItems {
	const root: TreeItem = { id: "root", children: [], text: "", canHaveChildren: true };
	const nodes: Record<string, TreeItem> = { [root.id]: root };
	const items = flattenedItems.map((item) => ({
		id: item.id,
		parentId: item.parentId,
		text: item.name,
		children: [],
		canHaveChildren: true,
	}));
	for (const item of items) {
		const { id, children, text, canHaveChildren } = item;
		const parentId = item.parentId ?? root.id;
		const parent = nodes[parentId] ?? findItem(items, parentId);

		nodes[id] = { id, children, text, canHaveChildren };
		parent.children.push({ id, children, text, canHaveChildren });
	}

	return root.children;
}

export const PreferencesList: FC = () => {
	const { t } = useTranslation();
	const { preferencesTree, emptyPreference, handleDrop } = usePreference();
	const [treeItems, setTreeItems] = useState(buildTree(preferencesTree));
	const { open, handleClose, handleOpen } = useOpen();
	const [preferenceId, setPreferenceId] = useState<Id | null>(null);

	useEffect(() => {
		setTreeItems(buildTree(preferencesTree));
	}, [preferencesTree]);

	const { updateEntity, deleteEntity, createEntity } = useLaravelCrudEntity(
		preferenceService,
		{
			messages: {
				create: t("Preference was saved."),
				update: t("Preference was saved."),
				delete: t("Preference was deleted."),
			},
			entityName: "Preference",
		},
		[preferenceService.allKey],
	);

	return (
		<>
			<Card>
				<CardHeader>
					<CardHeading>{t("Preferences")}</CardHeading>
					<CardHeaderActions>
						<>
							<IconButton title={t("New preference")} onClick={handleOpen} color="primary" size="small" icon="plus" />
							<PreferenceDrawer
								heading={t("New preference")}
								initialValues={emptyPreference}
								open={open}
								onSubmit={createEntity}
								handleClose={handleClose}
							/>
						</>
					</CardHeaderActions>
				</CardHeader>

				<LoadingBar loading={false} />

				{treeItems.length > 0 ? (
					<SortableTreeView
						treeItems={treeItems}
						onDrop={(newTreeItems) => {
							setTreeItems(newTreeItems);
							void handleDrop(newTreeItems);
						}}
						renderTreeNode={({ id, ...rest }) => (
							<PreferencesTreeNode {...rest} onEdit={() => setPreferenceId(id)} onRemove={() => deleteEntity(id)} />
						)}
						maxDepth={2}
					/>
				) : (
					<EmptyDataList>{t("No preferences available.")}</EmptyDataList>
				)}
			</Card>
			{preferenceId && (
				<EditPreferenceDrawer
					preferenceId={preferenceId}
					onSubmit={(values) => updateEntity(preferenceId, values)}
					handleClose={() => setPreferenceId(null)}
				/>
			)}
		</>
	);
};
